import { useState, useEffect } from 'react';
import kit from './kit.module.css';
import styles from './ClarificationDemo.module.css';

type State = 'question' | 'confirming' | 'done';

const OPTIONS = ['Professional & formal', 'Warm & approachable', 'Direct & concise'];

export function ClarificationDemo() {
  const [state, setState] = useState<State>('question');
  const [selected, setSelected] = useState<string | null>(null);
  const [customText, setCustomText] = useState('');

  const activeValue = selected ?? (customText.trim() || null);

  useEffect(() => {
    if (state !== 'confirming') return;
    const t = setTimeout(() => setState('done'), 1500);
    return () => clearTimeout(t);
  }, [state]);

  function handleOptionClick(option: string) {
    setSelected(option);
    setCustomText('');
  }

  function handleCustomChange(e: React.ChangeEvent<HTMLInputElement>) {
    setCustomText(e.target.value);
    setSelected(null);
  }

  function handleContinue() {
    if (!activeValue) return;
    setState('confirming');
  }

  return (
    <div className={kit.root}>
      <div className={kit.message}>Write Sarah's CV for her care coordinator applications.</div>

      {state === 'question' ? (
        <>
          <div className={kit.agent}>
            <span className={kit.agentIcon} aria-hidden="true">✦</span>
            <p>I have everything else I need. Just one question before I start.</p>
          </div>

          <div className={kit.proposal}>
            <div className={kit.proposalTop}>
              <span>One quick question</span>
              <span>1 / 1</span>
            </div>
            <h3 className={kit.proposalTitle}>What tone should the personal statement use?</h3>
            <p className={styles.sub}>This helps match the CV to the type of role Sarah is applying for.</p>
            <div className={kit.row} role="radiogroup" aria-label="Tone">
              {OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={selected === option}
                  className={`${kit.btn} ${selected === option ? styles.optionSelected : ''}`}
                  onClick={() => handleOptionClick(option)}
                >
                  {option}
                </button>
              ))}
            </div>
            <input
              className={styles.input}
              type="text"
              placeholder="Or describe the tone..."
              aria-label="Describe the tone"
              value={customText}
              onChange={handleCustomChange}
            />
            <button
              type="button"
              className={`${kit.btn} ${kit.btnPrimary}`}
              disabled={!activeValue}
              onClick={handleContinue}
            >
              Continue
            </button>
          </div>
          <p className={kit.feedback}>Pick an option or type your own. Everything else is already filled in.</p>
        </>
      ) : (
        <>
          <div className={kit.bubbleUser}>{activeValue}</div>
          <div className={kit.agent}>
            <span className={kit.agentIcon} aria-hidden="true">✦</span>
            <p>
              Got it. Writing the CV with a <strong>{activeValue!.toLowerCase()}</strong> personal statement.
            </p>
          </div>

          {state === 'confirming' ? (
            <p className={`${kit.feedback} ${styles.working}`} aria-live="polite">
              <span className={styles.spinner} aria-hidden="true" />
              Generating...
            </p>
          ) : (
            <>
              <div className={kit.proposal}>
                <div className={kit.proposalTop}>
                  <span>Ready to review</span>
                  <span>Sarah Mitchell</span>
                </div>
                <h3 className={kit.proposalTitle}>CV ready to review</h3>
                <dl className={kit.fields}>
                  <dt>Tone</dt>
                  <dd>{activeValue}</dd>
                  <dt>Asked</dt>
                  <dd>1 question</dd>
                </dl>
              </div>
              <p className={kit.feedback} aria-live="polite">One question, one answer, then straight to the task.</p>
            </>
          )}
        </>
      )}
    </div>
  );
}
